import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Toolbar,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  IconButton,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Grid,
  Divider,
  TablePagination,
  Chip,
  Alert,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Add, Delete, Edit, Visibility } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AdminSidebar from "../AdminSidebar";
import { useAuth } from "../App";
import { API_URL } from "../config";

function SectionEyebrow({ children, color }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.25,
        color,
        fontFamily: '"DM Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: "10.5px",
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        fontWeight: 500,
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 2,
          borderRadius: 2,
          bgcolor: "currentColor",
          flexShrink: 0,
        }}
      />
      {children}
    </Box>
  );
}

function getToken() {
  const auth = localStorage.getItem("ftl_auth");
  if (!auth) return null;
  try {
    return JSON.parse(auth).access_token;
  } catch {
    return null;
  }
}

function getStatusChipStyles(status, dark, accent) {
  const key = String(status || "").toLowerCase();

  if (key.includes("active") || key.includes("open")) {
    return {
      bg: dark ? "rgba(34,197,94,0.18)" : "rgba(34,197,94,0.10)",
      fg: dark ? "#86efac" : "#15803d",
      border: dark ? "rgba(34,197,94,0.24)" : "rgba(34,197,94,0.18)",
    };
  }

  if (key.includes("draft")) {
    return {
      bg: dark ? "rgba(148,163,184,0.16)" : "rgba(148,163,184,0.12)",
      fg: dark ? "#cbd5e1" : "#4b5563",
      border: dark ? "rgba(148,163,184,0.24)" : "rgba(148,163,184,0.18)",
    };
  }

  if (key.includes("closed") || key.includes("filled")) {
    return {
      bg: dark ? "rgba(148,163,184,0.16)" : "rgba(148,163,184,0.12)",
      fg: dark ? "#cbd5e1" : "#374151",
      border: dark ? "rgba(148,163,184,0.24)" : "rgba(148,163,184,0.18)",
    };
  }

  if (key.includes("hold") || key.includes("paused")) {
    return {
      bg: dark ? "rgba(245,158,11,0.18)" : "rgba(245,158,11,0.12)",
      fg: dark ? "#fcd34d" : "#854d0e",
      border: dark ? "rgba(245,158,11,0.24)" : "rgba(245,158,11,0.18)",
    };
  }

  return {
    bg: dark ? alpha(accent, 0.14) : alpha(accent, 0.08),
    fg: accent,
    border: alpha(accent, 0.2),
  };
}

function JobOrders() {
  const { fetchWithAuth } = useAuth() || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";

  const accent = dark ? "#fb923c" : "#f97316";
  const pageBg = dark ? "#07080f" : "#ffffff";
  const panelBg = dark ? "#0d1220" : "#ffffff";
  const text = dark ? "#e8f0fe" : "#0f0f1a";
  const mid = dark ? "#94a3b8" : "#4b4b6a";
  const muted = dark ? "#4b6080" : "#9898b8";
  const border = dark ? "rgba(99, 179, 237, 0.10)" : "#e8e8f0";
  const borderStrong = dark ? "rgba(99, 179, 237, 0.22)" : "#d4d4e4";

  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState({
    title: "",
    organization_id: "",
    department: "",
    location_ids: [],
    type_ids: [],
    recruiter_ids: [],
    openings: "",
    status: "Active",
    job_id: "",
    questions_id: [],
    start_date: "",
    end_date: "",
    days_old: "",
    requires_application_fee: false,
    application_fee_amount: "",
    application_fee_currency: "KES",
    application_fee_description: "",
  });

  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizationsError, setOrganizationsError] = useState("");

  const [formErrors, setFormErrors] = useState({});
  const [jobs, setJobs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [recruiters, setRecruiters] = useState([]);

  const [jobsLoading, setJobsLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [jobTypesLoading, setJobTypesLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [recruitersLoading, setRecruitersLoading] = useState(false);

  const [jobsError, setJobsError] = useState("");
  const [questionsError, setQuestionsError] = useState("");
  const [jobTypesError, setJobTypesError] = useState("");
  const [locationsError, setLocationsError] = useState("");
  const [recruitersError, setRecruitersError] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleViewDetails = (jobOrder) => {
    navigate(`/admin/job-orders/${jobOrder.id}`);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      if (
        name === "questions_id" ||
        name === "location_ids" ||
        name === "type_ids" ||
        name === "recruiter_ids"
      ) {
        let arr = value;
        if (typeof arr === "string") arr = arr.split(",");
        if (!Array.isArray(arr)) arr = [];
        return { ...prev, [name]: arr };
      }

      if (type === "checkbox") {
        return { ...prev, [name]: checked };
      }

      return { ...prev, [name]: value };
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!form.title || form.title.trim() === "") errors.title = "Title is required";
    if (!form.job_id) errors.job_id = "Job selection is required";

    if (form.requires_application_fee) {
      if (
        form.application_fee_amount === "" ||
        form.application_fee_amount === null ||
        Number(form.application_fee_amount) <= 0
      ) {
        errors.application_fee_amount =
          "Application fee amount must be greater than 0";
      }
    }

    return errors;
  };

  const handleOpenDialog = (job) => {
    setForm(
      job
        ? {
            title: job.title || "",
            organization_id: job.organization_id || "",
            department: job.department || "",
            location_ids:
              Array.isArray(job.location_ids) && job.location_ids.length > 0
                ? job.location_ids
                : Array.isArray(job.locations)
                ? job.locations.map((l) => l.id)
                : [],
            type_ids:
              Array.isArray(job.type_ids) && job.type_ids.length > 0
                ? job.type_ids
                : Array.isArray(job.job_types)
                ? job.job_types.map((t) => t.id)
                : [],
            recruiter_ids:
              Array.isArray(job.recruiter_ids) && job.recruiter_ids.length > 0
                ? job.recruiter_ids
                : Array.isArray(job.recruiters)
                ? job.recruiters.map((r) => r.id)
                : [],
            openings: job.openings || "",
            status: job.status || "Active",
            job_id: job.job_id || "",
            questions_id:
              Array.isArray(job.questions_id) && job.questions_id.length > 0
                ? job.questions_id
                : Array.isArray(job.questions)
                ? job.questions.map((q) => q.id)
                : [],
            start_date: job.start_date ? job.start_date.split("T")[0] : "",
            end_date: job.end_date ? job.end_date.split("T")[0] : "",
            days_old: job.days_old || "",
            requires_application_fee: !!job.requires_application_fee,
            application_fee_amount:
              job.application_fee_amount !== null &&
              job.application_fee_amount !== undefined
                ? job.application_fee_amount
                : "",
            application_fee_currency: job.application_fee_currency || "KES",
            application_fee_description: job.application_fee_description || "",
          }
        : {
            title: "",
            organization_id: "",
            department: "",
            location_ids: [],
            type_ids: [],
            recruiter_ids: [],
            openings: "",
            status: "Active",
            job_id: "",
            questions_id: [],
            start_date: "",
            end_date: "",
            days_old: "",
            requires_application_fee: false,
            application_fee_amount: "",
            application_fee_currency: "KES",
            application_fee_description: "",
          }
    );

    setEditing(job || null);
    setFormErrors({});
    setOpenDialog(true);

    fetchJobs();
    fetchQuestions();
    fetchLocations();
    fetchRecruiters();
    fetchJobTypes();
    fetchOrganizations();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditing(null);
    setFormErrors({});
    setForm({
      title: "",
      organization_id: "",
      department: "",
      location_ids: [],
      type_ids: [],
      recruiter_ids: [],
      openings: "",
      status: "Active",
      job_id: "",
      questions_id: [],
      start_date: "",
      end_date: "",
      days_old: "",
      requires_application_fee: false,
      application_fee_amount: "",
      application_fee_currency: "KES",
      application_fee_description: "",
    });
  };

  const fetchOrganizations = async () => {
    setOrganizationsLoading(true);
    setOrganizationsError("");
    try {
      const res = await fetchWithAuth(`${API_URL}/organizations/`);
      if (!res.ok) throw new Error("Failed to fetch organizations");
      const data = await res.json();
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (e) {
      setOrganizationsError(e.message);
      setOrganizations([]);
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const fetchJobTypes = async () => {
    setJobTypesLoading(true);
    setJobTypesError("");
    try {
      const res = await fetch(`${API_URL}/job-types/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed to fetch job types");
      const data = await res.json();
      setJobTypes(Array.isArray(data) ? data : []);
    } catch (e) {
      setJobTypesError(e.message);
      setJobTypes([]);
    } finally {
      setJobTypesLoading(false);
    }
  };

  const fetchLocations = async () => {
    setLocationsLoading(true);
    setLocationsError("");
    try {
      const res = await fetchWithAuth(`${API_URL}/locations/`);
      if (!res.ok) {
        if (res.status === 403) {
          setLocationsError(
            "You are not authorized to perform this action. Please contact your system administrator."
          );
          setLocations([]);
          return;
        }
        throw new Error("Failed to fetch locations");
      }
      const data = await res.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch (e) {
      setLocationsError(e.message);
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  const fetchRecruiters = async () => {
    setRecruitersLoading(true);
    setRecruitersError("");
    try {
      const res = await fetch(`${API_URL}/recruiters/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed to fetch recruiters");
      const data = await res.json();
      setRecruiters(Array.isArray(data) ? data : []);
    } catch (e) {
      setRecruitersError(e.message);
      setRecruiters([]);
    } finally {
      setRecruitersLoading(false);
    }
  };

  const fetchJobs = async () => {
    setJobsLoading(true);
    setJobsError("");
    try {
      const res = await fetch(`${API_URL}/jobs/active/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed to fetch jobs");
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      setJobsError(e.message);
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchQuestions = async () => {
    setQuestionsLoading(true);
    setQuestionsError("");
    try {
      const res = await fetch(`${API_URL}/questions/`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed to fetch questions");
      const data = await res.json();
      setQuestions(Array.isArray(data) ? data : []);
    } catch (e) {
      setQuestionsError(e.message);
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const fetchJobOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_URL}/job-orders/`);
      if (!res.ok) {
        if (res.status === 403) {
          setError(
            "You are not authorized to perform this action. Please contact your system administrator."
          );
          return;
        }
        throw new Error("Failed to fetch job orders");
      }
      const data = await res.json();
      setJobOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobOrders();
    fetchLocations();
    fetchJobTypes();
    fetchRecruiters();
    fetchJobs();
    fetchQuestions();
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const openParam = searchParams.get("open");
    if (openParam === "create") {
      handleOpenDialog(null);
    }
  }, [searchParams]);

  const filteredJobOrders = useMemo(() => {
    const s = search.toLowerCase().trim();
    return jobOrders.filter((job) => {
      if (!s) return true;
      return (
        job.title?.toLowerCase().includes(s) ||
        job.company_name?.toLowerCase().includes(s) ||
        job.department?.toLowerCase().includes(s) ||
        job.locations?.map((l) => l.name).join(", ").toLowerCase().includes(s) ||
        job.job_types?.map((t) => t.name).join(", ").toLowerCase().includes(s) ||
        job.recruiters
          ?.map((r) => r.full_name || r.name || r.email)
          .join(", ")
          .toLowerCase()
          .includes(s) ||
        job.status?.toLowerCase().includes(s)
      );
    });
  }, [jobOrders, search]);

  const handleSubmit = async () => {
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSnackbar({
        open: true,
        message: "Please fix form errors",
        severity: "error",
      });
      return;
    }

    const payload = { ...form };

    payload.start_date = form.start_date
      ? new Date(form.start_date + "T00:00:00").toISOString()
      : null;
    payload.end_date = form.end_date
      ? new Date(form.end_date + "T00:00:00").toISOString()
      : null;
    payload.questions_id = Array.isArray(form.questions_id) ? form.questions_id : [];
    payload.location_ids = Array.isArray(form.location_ids) ? form.location_ids : [];
    payload.type_ids = Array.isArray(form.type_ids) ? form.type_ids : [];
    payload.recruiter_ids = Array.isArray(form.recruiter_ids) ? form.recruiter_ids : [];
    payload.requires_application_fee = !!form.requires_application_fee;
    payload.application_fee_amount = form.requires_application_fee
      ? Number(form.application_fee_amount)
      : null;
    payload.application_fee_currency = form.requires_application_fee
      ? form.application_fee_currency || "KES"
      : "KES";
    payload.application_fee_description = form.requires_application_fee
      ? form.application_fee_description || ""
      : null;

    if (!payload.job_id) delete payload.job_id;
    if (!payload.organization_id) delete payload.organization_id;

    const url = editing
      ? `${API_URL}/job-orders/${editing.id}`
      : `${API_URL}/job-orders/`;
    const method = editing ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let errorMessage = "An error occurred while saving the job order";
        try {
          const errorData = await res.json();
          if (res.status === 409) {
            errorMessage =
              errorData.detail ||
              errorData.message ||
              "Cannot update job order: This job order has existing applications and cannot be modified.";
          } else if (errorData.detail || errorData.message) {
            errorMessage = errorData.detail || errorData.message;
          }
        } catch {
          errorMessage = res.statusText || errorMessage;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
        return;
      }

      setSnackbar({
        open: true,
        message: editing ? "Job order updated" : "Job order created",
        severity: "success",
      });

      handleCloseDialog();
      fetchJobOrders();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: "error" });
    }
  };

  const handleDelete = async (job) => {
    if (!window.confirm("Delete this job order?")) return;

    try {
      const res = await fetch(`${API_URL}/job-orders/${job.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (!res.ok) throw new Error("Failed to delete job order");

      setSnackbar({
        open: true,
        message: "Job order deleted",
        severity: "success",
      });
      fetchJobOrders();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: "error" });
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: pageBg }}>
      <AdminSidebar selected="Job Orders" />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pl: { xs: 1.5, md: 3 },
          pr: { xs: 1.5, md: 3 },
          pt: 2,
          pb: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Toolbar />

        <Container maxWidth="xl" disableGutters sx={{ mt: 1 }}>
          <Box
            sx={{
              mb: 3,
              overflow: "hidden",
              borderRadius: "28px",
              border: `1px solid ${border}`,
              background: dark
                ? `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, rgba(7,8,15,0.96) 45%, rgba(7,8,15,0.98) 100%)`
                : `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, rgba(255,255,255,0.98) 42%, rgba(248,248,251,0.98) 100%)`,
              boxShadow: dark
                ? "0 32px 80px rgba(0,0,0,0.30)"
                : "0 32px 80px rgba(15,15,26,0.08)",
            }}
          >
            <Box
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Box>
                <SectionEyebrow color={accent}>AjiraHQ · Job Orders</SectionEyebrow>
                <Typography
                  sx={{
                    mt: 2,
                    color: text,
                    fontWeight: 800,
                    fontSize: "clamp(30px, 4vw, 44px)",
                    lineHeight: 1.06,
                    letterSpacing: "-0.03em",
                    mb: 0.75,
                  }}
                >
                  Manage open roles and hiring intake.
                </Typography>
                <Typography
                  sx={{
                    color: mid,
                    fontSize: "15px",
                    lineHeight: 1.8,
                    maxWidth: 620,
                  }}
                >
                  Create and update job orders, assign recruiters, manage screening
                  questions, and monitor application volume by role.
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog(null)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1.1,
                  background: accent,
                  boxShadow: `0 14px 34px ${alpha(accent, 0.22)}`,
                  "&:hover": {
                    background: accent,
                    filter: "brightness(1.08)",
                  },
                }}
              >
                Add Job Order
              </Button>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "22px",
              background: panelBg,
              border: `1px solid ${border}`,
              boxShadow: dark
                ? "0 20px 50px rgba(0,0,0,0.20)"
                : "0 12px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 2,
              }}
            >
              <TextField
                size="small"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                placeholder="Search title, department, recruiter, type, location..."
                sx={{
                  width: { xs: "100%", sm: 380 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    backgroundColor: dark ? "rgba(8,12,24,0.7)" : "#ffffff",
                    transition: "all .2s ease",
                    "& fieldset": { borderColor: border },
                    "&:hover fieldset": { borderColor: borderStrong },
                    "&.Mui-focused": {
                      boxShadow: `0 0 0 4px ${alpha(accent, 0.12)}`,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: accent,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: text,
                  },
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: muted, fontSize: 20 }} />,
                }}
              />

              <Typography sx={{ fontSize: "13px", color: muted }}>
                {filteredJobOrders.length} result{filteredJobOrders.length !== 1 ? "s" : ""}
              </Typography>
            </Box>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 260,
                }}
              >
                <CircularProgress sx={{ color: accent }} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: "18px" }}>
                {error}
              </Alert>
            ) : (
              <>
                <TableContainer
                  sx={{
                    borderRadius: "18px",
                    border: `1px solid ${border}`,
                    overflowX: "auto",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          "Title",
                          "Department",
                          "Location",
                          "Status",
                          "Openings",
                          "Type",
                          "Applicants",
                          "Actions",
                        ].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              fontWeight: 700,
                              color: mid,
                              background: dark ? "#111827" : "#fafafa",
                              borderBottom: `1px solid ${border}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredJobOrders
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((job) => {
                          const statusStyles = getStatusChipStyles(job.status, dark, accent);

                          return (
                            <TableRow
                              key={job.id}
                              hover
                              sx={{
                                cursor: "pointer",
                                "& td": {
                                  borderBottom: `1px solid ${border}`,
                                  background: dark ? "rgba(13,18,32,0.96)" : "#ffffff",
                                  color: mid,
                                },
                                "&:hover td": {
                                  background: dark
                                    ? "rgba(255,255,255,0.03)"
                                    : "rgba(15,15,26,0.02)",
                                },
                              }}
                              onClick={(e) => {
                                if (e.target.closest("button") || e.target.closest("a")) return;
                                handleViewDetails(job);
                              }}
                            >
                              <TableCell sx={{ color: text, fontWeight: 700 }}>
                                {job.title}
                              </TableCell>

                              <TableCell>{job.department || "—"}</TableCell>

                              <TableCell>
                                {Array.isArray(job.locations) && job.locations.length > 0 ? (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {job.locations.map((l) => (
                                      <Chip
                                        key={l.id}
                                        label={l.name}
                                        size="small"
                                        icon={<LocationOnOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                                        sx={{
                                          borderRadius: 999,
                                          background: dark
                                            ? "rgba(255,255,255,0.04)"
                                            : "rgba(15,15,26,0.03)",
                                          color: mid,
                                          border: `1px solid ${border}`,
                                        }}
                                      />
                                    ))}
                                  </Box>
                                ) : job.location ? (
                                  <Chip
                                    label={job.location}
                                    size="small"
                                    icon={<LocationOnOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                                    sx={{
                                      borderRadius: 999,
                                      background: dark
                                        ? "rgba(255,255,255,0.04)"
                                        : "rgba(15,15,26,0.03)",
                                      color: mid,
                                      border: `1px solid ${border}`,
                                    }}
                                  />
                                ) : (
                                  <Typography sx={{ color: muted, fontSize: "13px" }}>
                                    None
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={job.status || "Unknown"}
                                  size="small"
                                  sx={{
                                    borderRadius: 999,
                                    height: 24,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    bgcolor: statusStyles.bg,
                                    color: statusStyles.fg,
                                    border: `1px solid ${statusStyles.border}`,
                                    textTransform: "capitalize",
                                  }}
                                />
                              </TableCell>

                              <TableCell>{job.openings ?? "—"}</TableCell>

                              <TableCell>
                                {Array.isArray(job.job_types) && job.job_types.length > 0 ? (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {job.job_types.map((t) => (
                                      <Chip
                                        key={t.id}
                                        label={t.name}
                                        size="small"
                                        icon={
                                          <BusinessCenterOutlinedIcon
                                            sx={{ fontSize: "16px !important" }}
                                          />
                                        }
                                        sx={{
                                          borderRadius: 999,
                                          background: dark
                                            ? "rgba(255,255,255,0.04)"
                                            : "rgba(15,15,26,0.03)",
                                          color: mid,
                                          border: `1px solid ${border}`,
                                        }}
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography sx={{ color: muted, fontSize: "13px" }}>
                                    None
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell>
                                {typeof job.candidates_count === "number"
                                  ? job.candidates_count
                                  : Array.isArray(job.applications)
                                  ? job.applications.length
                                  : 0}
                              </TableCell>

                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(job)}
                                  sx={{ color: mid, mr: 0.5 }}
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog(job)}
                                  sx={{ color: mid, mr: 0.5 }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(job)}
                                  sx={{ color: "#dc2626" }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={filteredJobOrders.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Rows per page:"
                    showFirstButton
                    showLastButton
                    sx={{
                      color: mid,
                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                        { color: mid },
                    }}
                  />
                </Box>
              </>
            )}
          </Paper>
        </Container>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            mb: 1,
            pb: 0,
            fontWeight: 800,
            mt: 2,
            color: text,
            letterSpacing: "-0.02em",
          }}
        >
          {editing ? "Edit Job Order" : "Add Job Order"}
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Title"
                name="title"
                value={form.title}
                onChange={handleChange}
                fullWidth
                required
                error={!!formErrors.title}
                helperText={formErrors.title}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.job_id}>
                <InputLabel id="job-label">Job</InputLabel>
                <Select
                  labelId="job-label"
                  name="job_id"
                  value={form.job_id}
                  label="Job"
                  onChange={handleChange}
                  disabled={jobsLoading}
                >
                  {jobsLoading ? (
                    <MenuItem value="">
                      <em>Loading...</em>
                    </MenuItem>
                  ) : jobsError ? (
                    <MenuItem value="">
                      <em>Error loading jobs</em>
                    </MenuItem>
                  ) : jobs.length === 0 ? (
                    <MenuItem value="">
                      <em>No jobs found</em>
                    </MenuItem>
                  ) : (
                    jobs.map((j) => (
                      <MenuItem key={j.id} value={j.id}>
                        {j.title}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.job_id && (
                  <Typography color="error" variant="caption">
                    {formErrors.job_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Department"
                name="department"
                value={form.department}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="organization-label">Organization</InputLabel>
                <Select
                  labelId="organization-label"
                  name="organization_id"
                  value={form.organization_id}
                  label="Organization"
                  onChange={handleChange}
                  disabled={organizationsLoading}
                >
                  {organizationsLoading ? (
                    <MenuItem value="">
                      <em>Loading...</em>
                    </MenuItem>
                  ) : organizationsError ? (
                    <MenuItem value="">
                      <em>Error loading organizations</em>
                    </MenuItem>
                  ) : organizations.length === 0 ? (
                    <MenuItem value="">
                      <em>No organizations found</em>
                    </MenuItem>
                  ) : (
                    organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="locations-label">Locations</InputLabel>
                <Select
                  labelId="locations-label"
                  multiple
                  name="location_ids"
                  value={Array.isArray(form.location_ids) ? form.location_ids : []}
                  onChange={handleChange}
                  input={<OutlinedInput label="Locations" />}
                  renderValue={(selected) =>
                    Array.isArray(selected) && Array.isArray(locations)
                      ? locations
                          .filter((loc) => selected.includes(loc.id))
                          .map((loc) => loc.name)
                          .join(", ")
                      : ""
                  }
                  disabled={locationsLoading}
                  error={!!locationsError}
                >
                  {locationsLoading ? (
                    <MenuItem value="">
                      <em>Loading...</em>
                    </MenuItem>
                  ) : locationsError ? (
                    <MenuItem value="">
                      <em>Error loading locations</em>
                    </MenuItem>
                  ) : locations.length === 0 ? (
                    <MenuItem value="">
                      <em>No locations found</em>
                    </MenuItem>
                  ) : (
                    locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        <Checkbox
                          checked={
                            Array.isArray(form.location_ids) &&
                            form.location_ids.indexOf(loc.id) > -1
                          }
                        />
                        <ListItemText primary={loc.name} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="types-label">Job Types</InputLabel>
                <Select
                  labelId="types-label"
                  multiple
                  name="type_ids"
                  value={Array.isArray(form.type_ids) ? form.type_ids : []}
                  onChange={handleChange}
                  input={<OutlinedInput label="Job Types" />}
                  renderValue={(selected) =>
                    Array.isArray(selected) && Array.isArray(jobTypes)
                      ? jobTypes
                          .filter((type) => selected.includes(type.id))
                          .map((type) => type.name)
                          .join(", ")
                      : ""
                  }
                  disabled={jobTypesLoading}
                  error={!!jobTypesError}
                >
                  {jobTypesLoading ? (
                    <MenuItem value="">
                      <em>Loading...</em>
                    </MenuItem>
                  ) : jobTypesError ? (
                    <MenuItem value="">
                      <em>Error loading job types</em>
                    </MenuItem>
                  ) : jobTypes.length === 0 ? (
                    <MenuItem value="">
                      <em>No job types found</em>
                    </MenuItem>
                  ) : (
                    jobTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Checkbox
                          checked={
                            Array.isArray(form.type_ids) &&
                            form.type_ids.indexOf(type.id) > -1
                          }
                        />
                        <ListItemText primary={type.name} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="recruiters-label">Recruiters</InputLabel>
                <Select
                  labelId="recruiters-label"
                  multiple
                  name="recruiter_ids"
                  value={Array.isArray(form.recruiter_ids) ? form.recruiter_ids : []}
                  onChange={handleChange}
                  input={<OutlinedInput label="Recruiters" />}
                  renderValue={(selected) =>
                    Array.isArray(selected) && Array.isArray(recruiters)
                      ? recruiters
                          .filter((rec) => selected.includes(rec.id))
                          .map((rec) => rec.full_name || rec.email)
                          .join(", ")
                      : ""
                  }
                  disabled={recruitersLoading}
                  error={!!recruitersError}
                >
                  {recruitersLoading ? (
                    <MenuItem value="">
                      <em>Loading...</em>
                    </MenuItem>
                  ) : recruitersError ? (
                    <MenuItem value="">
                      <em>Error loading recruiters</em>
                    </MenuItem>
                  ) : recruiters.length === 0 ? (
                    <MenuItem value="">
                      <em>No recruiters found</em>
                    </MenuItem>
                  ) : (
                    recruiters.map((rec) => (
                      <MenuItem key={rec.id} value={rec.id}>
                        <Checkbox
                          checked={
                            Array.isArray(form.recruiter_ids) &&
                            form.recruiter_ids.indexOf(rec.id) > -1
                          }
                        />
                        <ListItemText primary={rec.full_name || rec.email} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                name="start_date"
                type="date"
                value={form.start_date || ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    start_date: e.target.value || "",
                  }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                name="end_date"
                type="date"
                value={form.end_date || ""}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    end_date: e.target.value || "",
                  }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Openings"
                name="openings"
                value={form.openings}
                onChange={handleChange}
                fullWidth
                type="number"
                error={!!formErrors.openings}
                helperText={formErrors.openings}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  border: `1px solid ${border}`,
                  backgroundColor: dark ? "rgba(8,12,24,0.5)" : "#fafafa",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Checkbox
                  checked={!!form.requires_application_fee}
                  onChange={handleChange}
                  name="requires_application_fee"
                />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: text }}>
                    Requires application fee
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: muted }}>
                    Enable this if applicants must pay before submitting this job application.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {form.requires_application_fee && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Application Fee Amount"
                    name="application_fee_amount"
                    value={form.application_fee_amount}
                    onChange={handleChange}
                    fullWidth
                    type="number"
                    error={!!formErrors.application_fee_amount}
                    helperText={formErrors.application_fee_amount}
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Currency"
                    name="application_fee_currency"
                    value={form.application_fee_currency}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Fee Description"
                    name="application_fee_description"
                    value={form.application_fee_description}
                    onChange={handleChange}
                    fullWidth
                    placeholder="Application fee"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="questions-label">Questions</InputLabel>
                <Select
                  labelId="questions-label"
                  multiple
                  name="questions_id"
                  value={Array.isArray(form.questions_id) ? form.questions_id : []}
                  onChange={handleChange}
                  input={<OutlinedInput label="Questions" />}
                  renderValue={(selected) =>
                    Array.isArray(selected)
                      ? questions
                          .filter((q) => selected.includes(q.id))
                          .map((q) => q.text)
                          .join(", ")
                      : ""
                  }
                  disabled={questionsLoading}
                  error={!!questionsError}
                >
                  {questionsLoading ? (
                    <MenuItem value="">
                      <em>Loading...</em>
                    </MenuItem>
                  ) : questionsError ? (
                    <MenuItem value="">
                      <em>Error loading questions</em>
                    </MenuItem>
                  ) : questions.length === 0 ? (
                    <MenuItem value="">
                      <em>No questions found</em>
                    </MenuItem>
                  ) : (
                    questions.map((q) => (
                      <MenuItem key={q.id} value={q.id}>
                        <Checkbox
                          checked={
                            Array.isArray(form.questions_id) &&
                            form.questions_id.indexOf(q.id) > -1
                          }
                        />
                        <ListItemText primary={q.text} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}

export default JobOrders;